import {
  clearError,
  disposeMedia,
  el,
  refreshMetrics,
  revokeResult,
  showError,
  signalPlanChange,
  state,
  updateFormatUi,
} from "./core.js"
import { encodeOutput } from "./encoder.js"
import { openMediaFile } from "./media.js"
import { rebuildLoopingPreview, setupPreviewEngine, stopPreviewEngine } from "./preview.js"
import { renderTimeline, setDirection, setSpeed, setupTimeline, updateTimelineWidth } from "./timeline.js"
import { refreshCropMetrics, resetCrop, setupCropFrame, updateCropOverlay } from "./crop.js"
import { refreshWorkerControl, setupWorkerControls } from "./workers.js"

function isVideoFile(file) {
  return Boolean(file && (file.type.startsWith("video/") || /\.(mp4|m4v|mov|webm|mkv|avi|ogv)$/i.test(file.name)))
}

function waitForVideoMetadata(video, timeoutMs = 15000) {
  if (video.readyState >= 1 && Number.isFinite(video.duration)) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer)
      video.removeEventListener("loadedmetadata", ready)
      video.removeEventListener("error", failed)
    }
    const ready = () => { cleanup(); resolve() }
    const failed = () => {
      cleanup()
      reject(new Error("The browser could not play this video's format or codec."))
    }
    const timer = setTimeout(() => {
      cleanup()
      reject(new Error("Timed out while opening the native video preview."))
    }, timeoutMs)
    video.addEventListener("loadedmetadata", ready, { once: true })
    video.addEventListener("error", failed, { once: true })
  })
}

async function initializeNativeVideo(sourceUrl) {
  const video = el.videoPreview
  video.pause()
  video.hidden = false
  video.muted = true
  video.playsInline = true
  video.preload = "auto"
  const metadataReady = waitForVideoMetadata(video)
  video.src = sourceUrl
  video.load()
  await metadataReady
  if (state.sourceUrl !== sourceUrl) return

  if (!state.sourceDuration) {
    state.sourceWidth = video.videoWidth
    state.sourceHeight = video.videoHeight
    state.sourceDuration = video.duration
    state.trimStart = 0
    state.trimEnd = video.duration
    state.previewCurrentTime = 0
    resetCrop({ silent: true })
    updateTimelineWidth()
    renderTimeline(0)
    updateCropOverlay()
    refreshMetrics("Video opened. Reading exact frame timestamps…")
    refreshCropMetrics()
  }
  video.currentTime = state.trimStart
  video.playbackRate = state.speed
  if (state.previewPlaying) void video.play().catch(() => {
    el.statusText.textContent = "Video opened. Tap play if autoplay was blocked."
  })
}

async function loadVideoFile(file) {
  if (!isVideoFile(file)) {
    showError(new Error("Choose a valid video file."))
    return
  }

  clearError()
  revokeResult(false)
  disposeMedia()
  resetCrop({ silent: true })
  state.file = file
  state.sourceUrl = URL.createObjectURL(file)
  state.previewPlaying = true
  el.previewToggle.textContent = "❚❚"
  el.editor.hidden = false
  el.convertButton.disabled = true
  el.resultPreview.hidden = true
  el.livePreview.hidden = false
  el.emptyResult.hidden = false
  el.timelineThumbnails.innerHTML = '<div class="timeline-placeholder"></div>'
  refreshMetrics("Opening video preview…")

  const sourceUrl = state.sourceUrl
  void initializeNativeVideo(sourceUrl).catch((error) => {
    if (state.sourceUrl === sourceUrl) el.statusText.textContent = error instanceof Error ? error.message : String(error)
  })

  try {
    const media = await openMediaFile(file, (message) => {
      if (state.sourceUrl === sourceUrl) el.statusText.textContent = message
    })
    if (state.sourceUrl !== sourceUrl) {
      media.input?.dispose?.()
      return
    }
    state.mediaModule = media.module
    state.input = media.input
    state.videoTrack = media.videoTrack
    state.mediaReady = true
    state.sourceFirstTimestamp = media.firstTimestamp
    state.sourceWidth = media.width
    state.sourceHeight = media.height
    state.sourceDuration = media.duration
    state.sourceFps = media.fps
    state.sourceFrameTimestamps = media.timestamps
    state.sourceFrameDurations = media.durations
    state.trimStart = 0
    state.trimEnd = media.duration
    state.previewCurrentTime = 0
    state.direction = "forward"
    state.zoom = 1
    resetCrop({ silent: true })

    el.convertButton.disabled = false
    el.timelineViewport.scrollLeft = 0
    setDirection("forward", false)
    setSpeed(1, false)
    updateTimelineWidth()
    renderTimeline(0)
    updateCropOverlay()
    refreshMetrics(`Loaded ${media.frameCount} exact source frames at ${media.fps.toFixed(3)} average fps. Building looping output preview…`)
    refreshCropMetrics()
    refreshWorkerControl()
    await rebuildLoopingPreview()
  } catch (error) {
    if (state.sourceUrl !== sourceUrl) return
    state.mediaReady = false
    state.videoTrack = null
    el.convertButton.disabled = true
    showError(error)
  }
}

function bindFileInput() {
  el.videoInput.addEventListener("change", () => {
    const [file] = el.videoInput.files || []
    el.videoInput.value = ""
    void loadVideoFile(file)
  })

  for (const name of ["dragenter", "dragover"]) {
    el.dropzone.addEventListener(name, (event) => {
      event.preventDefault()
      el.dropzone.classList.add("active")
    })
  }
  for (const name of ["dragleave", "drop"]) {
    el.dropzone.addEventListener(name, (event) => {
      event.preventDefault()
      el.dropzone.classList.remove("active")
    })
  }
  el.dropzone.addEventListener("drop", (event) => {
    const file = Array.from(event.dataTransfer?.files || []).find(isVideoFile)
    void loadVideoFile(file)
  })
}

function settingsChanged(reason = "settings") {
  revokeResult(true)
  refreshMetrics()
  refreshCropMetrics()
  refreshWorkerControl()
  signalPlanChange(reason)
}

function bindSettings() {
  document.querySelectorAll("[data-format]").forEach((button) => {
    button.addEventListener("click", () => {
      if (state.format === button.dataset.format) return
      state.format = button.dataset.format
      updateFormatUi()
      settingsChanged("format")
    })
  })

  const settings = [
    el.fpsSlider,
    el.resolutionSlider,
    el.scaleSlider,
    el.smartTiming,
    el.morphSlider,
    el.maxFramesToggle,
    el.maxFramesSlider,
    el.gifQualitySlider,
    el.colorsSlider,
    el.ditherMethod,
    el.ditherSlider,
    el.webpQualitySlider,
    el.webpLossless,
    el.brightnessSlider,
    el.saturationSlider,
    el.contrastSlider,
    el.grayscaleSlider,
  ]
  settings.forEach((input) => {
    input.addEventListener("input", () => settingsChanged(input.id))
    input.addEventListener("change", () => settingsChanged(input.id))
  })

  el.convertButton.addEventListener("click", () => void encodeOutput())
  el.cancelButton.addEventListener("click", () => {
    state.cancelRequested = true
    el.statusText.textContent = "Cancelling active frame jobs…"
  })

  addEventListener("converter-crop-change", () => {
    revokeResult(true)
    refreshMetrics()
    refreshCropMetrics()
    refreshWorkerControl()
  })
}

function selfTests() {
  console.assert(document.querySelectorAll("[data-format]").length === 2, "GIF and WebP controls required")
  console.assert(document.querySelectorAll("[data-direction]").length === 3, "Three direction controls required")
  console.assert(document.querySelectorAll("[data-speed]").length === 7, "Seven speed presets required")
  console.assert(!el.maxFramesToggle.checked, "Maximum frames must default off")
  console.assert(Number(el.morphSlider.value) === 0, "Morphing must default off")
  console.assert(document.getElementById("workerMode"), "Worker mode control required")
  console.assert(document.getElementById("autoPreviewToggle"), "Auto preview control required")
  console.assert(document.getElementById("stopPreviewButton"), "Stop preview control required")
  console.assert(!document.getElementById("filtersGroup").open, "Filters must default collapsed")
}

let initialized = false

export function disposeApp() {
  stopPreviewEngine()
  revokeResult(false)
  disposeMedia()
}

export function initializeApp() {
  if (initialized) return
  initialized = true
  bindFileInput()
  bindSettings()
  setupTimeline()
  setupCropFrame()
  setupPreviewEngine()
  setupWorkerControls()
  updateFormatUi()
  refreshMetrics("Choose a video to begin.")
  refreshCropMetrics()
  setSpeed(1, false)
  selfTests()
  document.documentElement.dataset.appReady = "true"
  addEventListener("beforeunload", disposeApp, { once: true })
}
